import { type RefObject, useEffect } from 'react';

const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
	let element = node?.parentElement ?? null;
	let firstScrollable: HTMLElement | null = null;

	while (element) {
		const { overflowY } = getComputedStyle(element);
		if (overflowY === 'auto' || overflowY === 'scroll') {
			if (element.scrollHeight > element.clientHeight) return element;
			firstScrollable ??= element;
		}
		element = element.parentElement;
	}

	return firstScrollable;
};

export const useStickyTableHeader = (containerRef: RefObject<HTMLElement | null>, enabled: boolean) => {
	useEffect(() => {
		const container = containerRef.current;
		if (!enabled || !container) return undefined;

		const thead = container.querySelector<HTMLTableSectionElement>('thead');
		const scrollParent = getScrollParent(container);
		if (!thead || !scrollParent) return undefined;

		let frame = 0;

		const update = () => {
			frame = 0;
			const distancePastTop = scrollParent.getBoundingClientRect().top - container.getBoundingClientRect().top;
			const maxShift = container.clientHeight - thead.offsetHeight;
			const shift = Math.min(Math.max(distancePastTop, 0), Math.max(maxShift, 0));
			thead.style.transform = shift > 0 ? `translateY(${shift}px)` : '';
		};

		const schedule = () => {
			if (!frame) frame = requestAnimationFrame(update);
		};

		update();
		scrollParent.addEventListener('scroll', schedule, { passive: true });
		window.addEventListener('resize', schedule);

		const resizeObserver = new ResizeObserver(schedule);
		resizeObserver.observe(container);
		resizeObserver.observe(scrollParent);

		return () => {
			if (frame) cancelAnimationFrame(frame);
			scrollParent.removeEventListener('scroll', schedule);
			window.removeEventListener('resize', schedule);
			resizeObserver.disconnect();
			thead.style.transform = '';
		};
	}, [containerRef, enabled]);
};
